import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { getBrands, postBrand, editBrand } from "../../services/api/getAll";
import { showGlobalSnackbar } from "../../context/snack";

interface PromoCode {
  code: string;
  discount: number;
  type: "percent" | "fixed";
  isActive: boolean;
}

interface PackagePrice {
  id: string;
  nameEn: string;
  nameAr: string;
  price: number;
  category: "wash" | "deep" | "polish";
}

const DEFAULT_PACKAGES: PackagePrice[] = [
  { id: "exterior_wash", nameEn: "Exterior Wash", nameAr: "غسيل خارجي", price: 200, category: "wash" },
  { id: "interior_wash", nameEn: "Interior Wash", nameAr: "غسيل داخلي", price: 200, category: "wash" },
  { id: "full_wash", nameEn: "Full Wash (Interior & Exterior)", nameAr: "الغسيل الكامل (داخلي وخارجي)", price: 350, category: "wash" },
  { id: "mini_care", nameEn: "Mini Car Care", nameAr: "ميني كار كير", price: 650, category: "deep" },
  { id: "super_care", nameEn: "Super Car Care", nameAr: "سوبر كار كير", price: 950, category: "deep" },
  { id: "overhaul", nameEn: "TowHelp Car Care", nameAr: "توهيلب كار كير", price: 1500, category: "deep" },
  { id: "polish_wax", nameEn: "Polish + Wax", nameAr: "بوليش + واكس", price: 1200, category: "polish" },
  { id: "ceramic_prep", nameEn: "Ceramic Coating Prep", nameAr: "تجهيز طبقات الحماية", price: 1800, category: "polish" },
  { id: "scratch_removal", nameEn: "Scratch & Swirl Removal", nameAr: "معالجة الخدوش وآثار الغسيل", price: 2500, category: "polish" },
  { id: "headlight", nameEn: "Headlight Restoration", nameAr: "تلميع الفوانيس الأمامية", price: 600, category: "polish" },
];

const PromoPricingAdminScreen = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brandProfile, setBrandProfile] = useState<any>(null);
  const [packages, setPackages] = useState<PackagePrice[]>(DEFAULT_PACKAGES);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  
  // Dialog state for adding promo code
  const [openPromoDialog, setOpenPromoDialog] = useState(false);
  const [newPromoCode, setNewPromoCode] = useState<PromoCode>({
    code: "",
    discount: 10,
    type: "percent",
    isActive: true,
  });

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getBrands();
      if (data && data.length > 0) {
        const latest = data[data.length - 1];
        setBrandProfile(latest);
        
        // Parse dynamic settings from description if it exists
        if (latest.description && latest.description.trim().startsWith("{")) {
          try {
            const parsed = JSON.parse(latest.description);
            if (parsed.prices) {
              const updatedPackages = packages.map((pkg) => {
                if (parsed.prices[pkg.id] !== undefined) {
                  return { ...pkg, price: Number(parsed.prices[pkg.id]) };
                }
                return pkg;
              });
              setPackages(updatedPackages);
            }
            if (parsed.promos) {
              setPromoCodes(parsed.promos);
            }
          } catch (e) {
            console.error("Failed to parse pricing configuration JSON", e);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load brand settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handlePriceChange = (id: string, value: string) => {
    const numVal = Number(value);
    setPackages((prev) =>
      prev.map((pkg) => (pkg.id === id ? { ...pkg, price: isNaN(numVal) ? 0 : numVal } : pkg))
    );
  };

  const handleAddPromoCode = () => {
    if (!newPromoCode.code.trim()) {
      alert("Please enter a promo code");
      return;
    }
    const cleanCode = newPromoCode.code.trim().toUpperCase();
    if (promoCodes.some((p) => p.code === cleanCode)) {
      alert("Promo code already exists");
      return;
    }
    setPromoCodes((prev) => [...prev, { ...newPromoCode, code: cleanCode }]);
    setOpenPromoDialog(false);
    setNewPromoCode({ code: "", discount: 10, type: "percent", isActive: true });
  };

  const handleDeletePromoCode = (code: string) => {
    setPromoCodes((prev) => prev.filter((p) => p.code !== code));
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      
      // Serialize pricing and promos
      const pricesMap: Record<string, number> = {};
      packages.forEach((pkg) => {
        pricesMap[pkg.id] = pkg.price;
      });

      const configPayload = {
        prices: pricesMap,
        promos: promoCodes,
      };

      const serializedConfig = JSON.stringify(configPayload);

      // Re-use current brand profile accounts, or use defaults
      const payload = {
        facebookAccount: brandProfile?.facebookAccount || "https://facebook.com/towhelp",
        instagramAccount: brandProfile?.instagramAccount || "https://instagram.com/towhelp",
        tikTokAccount: brandProfile?.tikTokAccount || "https://tiktok.com/@towhelp",
        appleStoreLink: brandProfile?.appleStoreLink || "https://apps.apple.com",
        googlePlayStoreLink: brandProfile?.googlePlayStoreLink || "https://play.google.com",
        title: brandProfile?.title || "TowHelp Settings",
        description: serializedConfig, // inject serialized config here
      };

      if (brandProfile?.id) {
        await editBrand(brandProfile.id, { ...payload, id: brandProfile.id });
      } else {
        const res = await postBrand(payload);
        setBrandProfile(res);
      }
      
      // Save locally as backup
      localStorage.setItem("dynamic_prices", JSON.stringify(pricesMap));
      localStorage.setItem("dynamic_promos", JSON.stringify(promoCodes));

      // Show success
      if (showGlobalSnackbar) {
        showGlobalSnackbar("Pricing and Promo settings saved successfully!", "success");
      } else {
        alert("Settings saved successfully!");
      }
      
      // Reload to ensure state is clean
      await loadSettings();
    } catch (err) {
      console.error("Failed to save settings:", err);
      alert("Error saving settings. Saving to local storage backup instead.");
      
      // Fallback local save
      const pricesMap: Record<string, number> = {};
      packages.forEach((pkg) => {
        pricesMap[pkg.id] = pkg.price;
      });
      localStorage.setItem("dynamic_prices", JSON.stringify(pricesMap));
      localStorage.setItem("dynamic_promos", JSON.stringify(promoCodes));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress size={50} sx={{ color: "#FFAE00" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh", color: "#fff" }}>
      {/* Title section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="800" sx={{ color: "#FFAE00" }}>
            Promo Codes & Service Pricing
          </Typography>
          <Typography variant="body2" sx={{ color: "#aaa", mt: 0.5 }}>
            Manage discounts and adjust the pricing of car wash packages dynamically.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveAll}
          disabled={saving}
          sx={{
            background: "linear-gradient(135deg, #FFAE00 0%, #FF8F00 100%)",
            color: "#000",
            fontWeight: "bold",
            px: 4,
            py: 1.5,
            borderRadius: "12px",
            boxShadow: "0 4px 15px rgba(255, 174, 0, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #ffc107 0%, #FFAE00 100%)",
              boxShadow: "0 6px 20px rgba(255, 174, 0, 0.4)",
            },
          }}
        >
          {saving ? "Saving Settings..." : "Save Settings"}
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Pricing Panel */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper
            className="premium-service-card"
            sx={{
              p: 4,
              backgroundColor: "rgba(18, 18, 18, 0.6) !important",
              border: "1px solid rgba(255, 174, 0, 0.15) !important",
              borderRadius: "20px !important",
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ color: "#FFAE00", mb: 3 }}>
              Car Wash & Care Packages Pricing (EGP)
            </Typography>

            <Box display="flex" flexDirection="column" gap={3}>
              {/* Category: Wash */}
              <Box>
                <Typography variant="subtitle2" fontWeight="800" sx={{ color: "#aaa", mb: 2, textTransform: "uppercase" }}>
                  🚿 Standard Wash
                </Typography>
                <Grid container spacing={2}>
                  {packages
                    .filter((p) => p.category === "wash")
                    .map((pkg) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={pkg.id}>
                        <Card sx={{ bgcolor: "rgba(30, 30, 30, 0.4)", border: "1px solid #333", borderRadius: "12px" }}>
                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                              {pkg.nameEn} ({pkg.nameAr})
                            </Typography>
                            <TextField
                              size="small"
                              type="number"
                              value={pkg.price}
                              onChange={(e) => handlePriceChange(pkg.id, e.target.value)}
                              InputProps={{
                                endAdornment: <Typography variant="caption" sx={{ color: "#aaa" }}>EGP</Typography>,
                              }}
                              sx={{
                                input: { color: "#fff" },
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": { borderColor: "#444" },
                                  "&:hover fieldset": { borderColor: "#FFAE00" },
                                  "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
                                },
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              </Box>

              <Divider sx={{ bgcolor: "#333" }} />

              {/* Category: Deep Care */}
              <Box>
                <Typography variant="subtitle2" fontWeight="800" sx={{ color: "#aaa", mb: 2, textTransform: "uppercase" }}>
                  ✨ Deep Car Care
                </Typography>
                <Grid container spacing={2}>
                  {packages
                    .filter((p) => p.category === "deep")
                    .map((pkg) => (
                      <Grid size={{ xs: 12, sm: 4 }} key={pkg.id}>
                        <Card sx={{ bgcolor: "rgba(30, 30, 30, 0.4)", border: "1px solid #333", borderRadius: "12px" }}>
                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Typography variant="caption" display="block" sx={{ color: "#fff", fontWeight: 600, mb: 1, minHeight: 40 }}>
                              {pkg.nameEn}
                            </Typography>
                            <TextField
                              size="small"
                              type="number"
                              value={pkg.price}
                              onChange={(e) => handlePriceChange(pkg.id, e.target.value)}
                              InputProps={{
                                endAdornment: <Typography variant="caption" sx={{ color: "#aaa" }}>EGP</Typography>,
                              }}
                              sx={{
                                input: { color: "#fff" },
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": { borderColor: "#444" },
                                  "&:hover fieldset": { borderColor: "#FFAE00" },
                                  "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
                                },
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              </Box>

              <Divider sx={{ bgcolor: "#333" }} />

              {/* Category: Polish */}
              <Box>
                <Typography variant="subtitle2" fontWeight="800" sx={{ color: "#aaa", mb: 2, textTransform: "uppercase" }}>
                  🏎️ Polishing & Protection
                </Typography>
                <Grid container spacing={2}>
                  {packages
                    .filter((p) => p.category === "polish")
                    .map((pkg) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={pkg.id}>
                        <Card sx={{ bgcolor: "rgba(30, 30, 30, 0.4)", border: "1px solid #333", borderRadius: "12px" }}>
                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, mb: 1 }}>
                              {pkg.nameEn}
                            </Typography>
                            <TextField
                              size="small"
                              type="number"
                              value={pkg.price}
                              onChange={(e) => handlePriceChange(pkg.id, e.target.value)}
                              InputProps={{
                                endAdornment: <Typography variant="caption" sx={{ color: "#aaa" }}>EGP</Typography>,
                              }}
                              sx={{
                                input: { color: "#fff" },
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": { borderColor: "#444" },
                                  "&:hover fieldset": { borderColor: "#FFAE00" },
                                  "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
                                },
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Promo Codes Panel */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper
            className="premium-service-card"
            sx={{
              p: 4,
              backgroundColor: "rgba(18, 18, 18, 0.6) !important",
              border: "1px solid rgba(255, 174, 0, 0.15) !important",
              borderRadius: "20px !important",
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#FFAE00" }}>
                Promo Codes
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setOpenPromoDialog(true)}
                sx={{
                  borderColor: "#FFAE00",
                  color: "#FFAE00",
                  "&:hover": {
                    borderColor: "#FF8F00",
                    background: "rgba(255, 174, 0, 0.08)",
                  },
                }}
              >
                Add Promo
              </Button>
            </Box>

            <TableContainer sx={{ border: "1px solid #333", borderRadius: "12px", overflow: "hidden" }}>
              <Table>
                <TableHead sx={{ bgcolor: "rgba(30, 30, 30, 0.7)" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#FFAE00", fontWeight: "bold", borderBottom: "1px solid #333" }}>Code</TableCell>
                    <TableCell sx={{ color: "#FFAE00", fontWeight: "bold", borderBottom: "1px solid #333" }}>Discount</TableCell>
                    <TableCell sx={{ color: "#FFAE00", fontWeight: "bold", borderBottom: "1px solid #333" }}>Type</TableCell>
                    <TableCell sx={{ color: "#FFAE00", fontWeight: "bold", borderBottom: "1px solid #333", textAlign: "right" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {promoCodes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: "#888", py: 4, borderBottom: "none" }}>
                        No active promo codes. Click "Add Promo" to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    promoCodes.map((promo) => (
                      <TableRow key={promo.code} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.02)" } }}>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold", borderBottom: "1px solid #222" }}>
                          {promo.code}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", borderBottom: "1px solid #222" }}>
                          {promo.discount}
                        </TableCell>
                        <TableCell sx={{ color: "#aaa", borderBottom: "1px solid #222", textTransform: "capitalize" }}>
                          {promo.type === "percent" ? "% Off" : "EGP Off"}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #222", textAlign: "right" }}>
                          <IconButton onClick={() => handleDeletePromoCode(promo.code)} color="error" size="small">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Promo Code Dialog */}
      <Dialog
        open={openPromoDialog}
        onClose={() => setOpenPromoDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            color: "#fff",
            border: "1px solid rgba(255, 174, 0, 0.2)",
            borderRadius: "16px",
            minWidth: 320,
          },
        }}
      >
        <DialogTitle sx={{ color: "#FFAE00", fontWeight: "bold" }}>Create Promo Code</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
          <TextField
            autoFocus
            label="Promo Code"
            fullWidth
            placeholder="e.g. TOWHELP20"
            value={newPromoCode.code}
            onChange={(e) => setNewPromoCode((prev) => ({ ...prev, code: e.target.value }))}
            inputProps={{ style: { textTransform: "uppercase" } }}
            sx={{
              input: { color: "#fff" },
              label: { color: "#aaa" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#444" },
                "&:hover fieldset": { borderColor: "#FFAE00" },
                "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
              },
            }}
          />

          <FormControl fullWidth>
            <InputLabel sx={{ color: "#aaa" }}>Discount Type</InputLabel>
            <Select
              value={newPromoCode.type}
              label="Discount Type"
              onChange={(e) =>
                setNewPromoCode((prev) => ({ ...prev, type: e.target.value as any }))
              }
              sx={{
                color: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#FFAE00" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FFAE00" },
              }}
            >
              <MenuItem value="percent">Percentage Discount (%)</MenuItem>
              <MenuItem value="fixed">Fixed Price Discount (EGP)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Discount Value"
            type="number"
            fullWidth
            value={newPromoCode.discount}
            onChange={(e) => setNewPromoCode((prev) => ({ ...prev, discount: Number(e.target.value) }))}
            sx={{
              input: { color: "#fff" },
              label: { color: "#aaa" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#444" },
                "&:hover fieldset": { borderColor: "#FFAE00" },
                "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenPromoDialog(false)} sx={{ color: "#aaa" }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddPromoCode}
            variant="contained"
            sx={{
              bgcolor: "#FFAE00",
              color: "#000",
              fontWeight: "bold",
              "&:hover": { bgcolor: "#FF8F00" },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromoPricingAdminScreen;
